import { Fragment } from "react";
import type { ProjectPath, TaskId, TaskView } from "../api/generated";
import type { Grouping } from "../domain/views";
import { calendarKey, groupHeading } from "../domain/when";
import { TaskRow } from "./TaskRow";

interface TaskListProps {
  tasks: TaskView[];
  grouping: Grouping;
  selectedId: TaskId | undefined;
  now: Date;
  onSelect(id: TaskId): void;
  onOpenTag(tag: string): void;
  onOpenProject(path: ProjectPath): void;
  onToggleComplete(view: TaskView): void;
  onRestore(view: TaskView): void;
}

interface TaskGroup {
  key: string;
  heading: string | undefined;
  views: TaskView[];
}

export function groupTasks(tasks: TaskView[], grouping: Grouping, now: Date): TaskGroup[] {
  if (grouping === "none") return [{ key: "all", heading: undefined, views: tasks }];
  const groups: TaskGroup[] = [];
  for (const view of tasks) {
    const key = view.task.due === undefined ? "undated" : calendarKey(view.task.due);
    const last = groups.at(-1);
    if (last?.key === key) {
      last.views.push(view);
    } else {
      groups.push({
        key,
        heading: key === "undated" ? "No date" : groupHeading(key, now),
        views: [view],
      });
    }
  }
  return groups;
}

export function TaskList({
  tasks,
  grouping,
  selectedId,
  now,
  onSelect,
  onOpenTag,
  onOpenProject,
  onToggleComplete,
  onRestore,
}: TaskListProps) {
  return (
    <>
      {groupTasks(tasks, grouping, now).map((group) => (
        <Fragment key={group.key}>
          {group.heading !== undefined && <h3 className="group-heading">{group.heading}</h3>}
          <ul className="task-list" aria-label={group.heading ?? "Tasks"}>
            {group.views.map((view) => (
              <TaskRow
                key={view.task.id}
                view={view}
                selected={view.task.id === selectedId}
                now={now}
                onSelect={() => onSelect(view.task.id)}
                onOpenTag={onOpenTag}
                onOpenProject={onOpenProject}
                onToggleComplete={() => onToggleComplete(view)}
                onRestore={() => onRestore(view)}
              />
            ))}
          </ul>
        </Fragment>
      ))}
    </>
  );
}
